/**
 * The problems service (W2-12a checker-runtime; contract `CreateProblemsService` in app/registry/types.ts;
 * DESIGN.md §5.10, §4.18; ARCHITECTURE.md §9).
 *
 * Feeds the analysis worker and keeps the problem list of the open chart:
 *  - on project open (a new DocStore from `getStore`): `init` with the serialized document, its seq, the thresholds
 *    and enabled rules (Settings > Checker), the song length, the Phira player settings, the active skin's metrics,
 *    the 1920 x 1080 reference viewport and the experience level (AnalysisWorkerSchema, CR-W2-12a-4), plus
 *    `configure { shaderErrors }` when the preview already reported some;
 *  - every committed transaction is posted in order (`txn {seq, ops}`); a document load, and the worker's
 *    `needResync`, post a full `resync` snapshot (deferred while a gesture is live, so uncommitted ops never reach
 *    the replica; released by the gesture's commit or cancel, or, for a gesture that ends with nothing to commit,
 *    by a short `gestureActive` poll);
 *  - settings, skin, player settings, song length with the asset it was decoded from (polled from the audio engine;
 *    CheckContext.songLengthAsset, CR-W2-12b-5) and the preview's shader compile errors are re-sent with `configure`
 *    when they change;
 *  - `problems` diffs (by problem id) are applied to the list as they stream in; `stats` ends a pass ("checking"
 *    status). Passes are incremental (only what a change can affect is checked again) and the Phira rules run first:
 *    stats with `phase: 'phira'` (CR-W3-08-2) say those are done for the request they started with, which ends
 *    `checking()` (what the export preflight waits for: only Phira rules can block an export) while
 *    `status().checking` stays on until the whole pass is done (UX-092).
 *  - Every request that changes what the worker checks (init, txn, resync, configure) carries a request token
 *    (`req`, CR-W2-12a-4) and the worker's stats name the token their pass started with, so "Checking..." and the
 *    "Fixed. Checking again" note stay until a pass covered the latest change, not just any pass that ended.
 *  - Project switches: the list is cleared at once and the worker's `init` answers with a reset diff
 *    (`PROBLEMS_RESET_ID`, core/check/session.ts) ahead of the new project's results. Until that reset arrives, events
 *    still in flight from the previous project (problems, stats, needResync) are dropped, so neither its problems nor
 *    its "checked" status leak into the new chart; the reset also clears whatever arrived before it.
 * Problems the document ignores ("Ignore here", `editor.ignoredProblems`) are left out at once, before the next pass.
 * Files the chart uses whose bytes the browser lost are found here, not by the worker (it cannot see the browser's
 * storage): `persist.hasAsset` probes them and each missing one is a fatal PH-01 / PH-02 problem (storedFiles.ts,
 * change request CR-W3-07-1).
 *
 * Answers the shell and the panels: problems (most severe first), counts, subscribe, rules (for Settings > Checker),
 * next / previous after the playhead (Alt+] / Alt+[, time order, continuing from the current problem), the quick-fix
 * target (Ctrl+.), `applyFix` (runs the fix command through the registry with source 'fix' while the
 * `problems.fixing` atom holds, so the fix's label names the history entry), the Problems panel filters (persisted in
 * `ext.checker.filter`), field badges for the inspector (`problemsAt`, `fieldBadges`) and "Ignore here".
 *
 * The contract's deps give the worker client, the store getter and the settings; the rest of the app (registry, UI,
 * editor store, skins, audio, engine, service swaps) arrives with `attach(services)`, called by the checker module's
 * `init`. Before that the service still feeds the worker (with default skin metrics) and runs its own fixes directly.
 */

import type { AppServices, CreateProblemsService, ProblemsService } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import { CHECK_ENVIRONMENT_DEFAULTS, enabledRuleIds } from '../../core/check/context';
import { REFERENCE_VIEWPORT } from '../../core/check/geometry';
import { isResetDiff, type AnalysisStats, type ShaderErrorReport } from '../../core/check/session';
import type { CheckCategory, CheckRule, CheckThresholds, Problem, ProblemDiff, Severity } from '../../core/check/types';
import { serializeDoc } from '../../core/doc/serialize';
import type { PhiraPlayerSettings, SkinMetrics } from '../../core/engine/types';
import type { AssetId, EffectId, KeyId, LineId, NoteId, TrackId } from '../../core/model/types';
import type { ChangeSet, Txn } from '../../core/ops/types';
import type { TempoMap } from '../../core/time/types';
import type { Json, MessageRef, Unsubscribe } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import type { BadgeProps } from '../../ui/types';
import type { AnalysisWorkerEvents, AnalysisWorkerSchema } from '../../workers/protocol';
import { m } from './checker.messages';
import { isFixCommand, runFix } from './fixes';
import {
  ALL_CATEGORIES,
  ALL_SEVERITIES,
  DEFAULT_FILTER,
  bySeverity,
  byTime,
  canGo,
  countBySeverity,
  ignoreProblemInDoc,
  playheadSec,
  problemBeatF64,
  revealProblem,
  tempoOf,
  unignoreInDoc,
  type ProblemFilter,
} from './navigation';
import { STORED_RECHECK_MS, StoredFilesProbe, storedMissingProblem, touchesAssets } from './storedFiles';

// ---------------------------------------------------------------------------------------------------------------
// Rules (the same files the analysis worker runs)
// ---------------------------------------------------------------------------------------------------------------

const ruleModules = import.meta.glob<{ readonly default: readonly CheckRule[] }>(
  '/src/features/checker/rules/*.rules.ts',
  { eager: true },
);

/** Every rule of `src/features/checker/rules/*.rules.ts`, sorted by id. */
export const CHECK_RULES: readonly CheckRule[] = Object.freeze(
  Object.keys(ruleModules)
    .sort()
    .flatMap((path) => ruleModules[path]?.default ?? [])
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)),
);

// ---------------------------------------------------------------------------------------------------------------
// Public extension of the contract
// ---------------------------------------------------------------------------------------------------------------

/** What an inspector field (or entity) badge lookup asks for. */
export interface ProblemQuery {
  /** Inspector field path, e.g. 'note.speed', 'line.alpha'. */
  readonly field?: string;
  readonly line?: LineId;
  readonly note?: NoteId;
  readonly key?: KeyId;
  readonly track?: TrackId;
  readonly asset?: AssetId;
  readonly effect?: EffectId;
}

export interface CheckerStatus {
  /** A chart is open and its replica was sent to the analysis worker. */
  readonly connected: boolean;
  /** Changes were sent and the pass that covers them has not finished yet (results may still be coming in). */
  readonly checking: boolean;
  /**
   * The Phira rules of that pass (the only ones that can block an export) have not finished yet: what
   * `ProblemsService.checking()` answers.
   */
  readonly checkingPhira: boolean;
  /** Facts of the last completed pass. */
  readonly stats: AnalysisStats | null;
  /** The analysis worker failed (problems are no longer updated). */
  readonly workerFailed: boolean;
  /** Rules that threw in the last pass (their earlier problems are kept); empty when every check ran. */
  readonly failedRules: readonly string[];
}

/** The checker's ProblemsService with the extras the checker UI and the inspector use (feature-detected). */
export interface CheckerProblemsService extends ProblemsService {
  readonly isCheckerService: true;
  /** Connects the rest of the app (module init); returns the disconnect function. */
  attach(s: AppServices): () => void;
  dispose(): void;
  /** Bumps on every change of problems, filter, status or current problem (React snapshots). */
  readonly version: number;
  status(): CheckerStatus;
  get(id: string): Problem | undefined;
  filter(): ProblemFilter;
  setFilter(patch: Partial<ProblemFilter>): void;
  /** Problem the user is at (last navigated, selected in the panel or fixed). */
  current(): string | null;
  setCurrent(id: string | null): void;
  /** Problems a fix was applied to and that wait for the next pass. */
  resolving(): ReadonlySet<string>;
  /** True while `applyFix` runs a fix command (module when-atom `problems.fixing`). */
  fixing(): boolean;
  /** Label of the fix being applied. */
  pendingFixLabel(): MessageRef | null;
  /** f64 beat of a problem (null without a time). */
  beatOf(p: Problem): number | null;
  /** Problems located at a field and/or entity (inspector badges). */
  problemsAt(q: ProblemQuery): readonly Problem[];
  /** Badges for an inspector field: one per problem, most severe first; clicking one shows it in Problems. */
  fieldBadges(q: ProblemQuery): readonly BadgeProps[];
  /** "Go" to a problem. */
  reveal(id: string): Promise<boolean>;
  /**
   * The problem Ctrl+. acts on: the current one (fixed, or gone to when it has no fix), else the most severe one
   * with a fix at the selection.
   */
  quickFixTarget(): Problem | null;
  ignore(id: string): boolean;
  unignore(keys: readonly string[]): boolean;
  /** Suppressions stored in the open chart. */
  ignoredKeys(): readonly string[];
  /** Custom shaders that failed to compile in the preview (PH-33), re-sent to the worker. */
  reportShaderErrors(errors: readonly ShaderErrorReport[]): void;
}

export function asCheckerService(p: ProblemsService): CheckerProblemsService | null {
  return (p as Partial<CheckerProblemsService>).isCheckerService === true ? (p as CheckerProblemsService) : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------------------------------------------

export interface ProblemsServiceOptions {
  /** Rule set (default: every *.rules.ts). */
  readonly rules?: readonly CheckRule[];
  /** Interval of the song-length poll (ms); 0 disables it. */
  readonly pollMs?: number;
  /** Interval at which a resync deferred by a live gesture checks whether the gesture ended (ms). */
  readonly resyncPollMs?: number;
  readonly setInterval?: (fn: () => void, ms: number) => unknown;
  readonly clearInterval?: (handle: unknown) => void;
}

const FILTER_KEY = 'checker.filter';
const NO_RULES: readonly string[] = Object.freeze([]);

/** A worker request with its request token (`req`, AnalysisWorkerSchema since CR-W2-12a-4; always set here). */
type Tokened<T> = T & { readonly req: number };
const SONG_POLL_MS = 1000;
/**
 * A resync deferred by a live gesture is released by the commit, or by the cancel's change set; a gesture that ends
 * with nothing to commit announces neither, so the service also looks at `gestureActive` this often meanwhile.
 */
export const RESYNC_POLL_MS = 50;
/** Two beats closer than this are the same place (the playhead sits exactly on a revealed problem). */
const SAME_BEAT = 1e-6;

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** True when two problem lists are the same objects in the same (sorted) order — a no-op rebuild. */
function sameList(a: readonly Problem[], b: readonly Problem[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function parseFilter(raw: Json | undefined): Omit<ProblemFilter, 'query'> {
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) return DEFAULT_FILTER;
  const o = raw as Readonly<Record<string, Json>>;
  const pick = <T extends string>(v: Json | undefined, all: readonly T[]): readonly T[] => {
    if (!Array.isArray(v)) return all;
    return all.filter((x) => (v as readonly Json[]).includes(x));
  };
  return {
    severities: pick<Severity>(o.severities, ALL_SEVERITIES),
    categories: pick<CheckCategory>(o.categories, ALL_CATEGORIES),
    scope: o.scope === 'line' ? 'line' : 'chart',
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------------------------------------------

type Deps = Parameters<CreateProblemsService>[0];

class CheckerProblemsServiceImpl implements CheckerProblemsService {
  readonly isCheckerService = true as const;

  private readonly deps: Deps;
  private readonly ruleList: readonly CheckRule[];
  private ruleInfo: readonly Pick<CheckRule, 'id' | 'title' | 'category' | 'severity' | 'level'>[] | null = null;
  private readonly opts: ProblemsServiceOptions;

  private services: AppServices | null = null;
  private readonly detachers: Unsubscribe[] = [];
  private readonly workerSubs: Unsubscribe[] = [];

  /** Problems from the worker, by id (including ones the document ignores since the last pass). */
  private readonly byId = new Map<string, Problem>();
  private listCache: readonly Problem[] | null = null;
  /** The cache needs a rebuild (the previous one is served until then — CR-W3-03-11). */
  private listDirty = false;
  /** A coalesced notification is scheduled (CR-W3-03-11: at most one per animation frame). */
  private notifyPending = false;
  private notifyHandle: unknown = null;
  private countsCache: Readonly<Record<Severity, number>> | null = null;
  private tempoCache: { readonly key: unknown; readonly map: TempoMap | null } | null = null;
  private readonly listeners = new Set<() => void>();
  private ver = 0;

  private store: DocStore | null = null;
  private storeSubs: Unsubscribe[] = [];
  private lastPostedSeq = -1;
  private pendingResync = false;
  private passRunning = false;
  /** The Phira rules of the pass that covers the latest request have not finished (export preflight). */
  private phiraRunning = false;
  private stats: AnalysisStats | null = null;
  private failedRules: readonly string[] = NO_RULES;
  private workerFailed = false;
  private poll: unknown = null;
  /** Watches for the end of the gesture a resync waits for (see RESYNC_POLL_MS). */
  private resyncPoll: unknown = null;
  /** Last request token sent (tokens grow by one per request). */
  private reqSeq = 0;
  /** This service initialised the worker before: it may still send the previous project's events. */
  private initPosted = false;
  /** Waiting for the reset diff of the latest `init`: events of the previous project are dropped. */
  private awaitingReset = false;
  /** Presence of the open chart's stored files (needs the persistence client: attached and a chart open). */
  private storedProbe: StoredFilesProbe | null = null;
  /** Re-probes the missing stored files while there are some. */
  private storedPoll: unknown = null;
  /** Their problems (rebuilt with the list). */
  private storedCache: readonly Problem[] | null = null;

  /** Last values sent to the worker (configure only sends changes). */
  private sent: {
    thresholds: CheckThresholds;
    enabled: readonly string[];
    level: 'guided' | 'full';
    player: PhiraPlayerSettings;
    skin: SkinMetrics;
    songLengthSec: number | null;
    songLengthAsset: AssetId | null;
  } | null = null;

  private filterState: ProblemFilter;
  private cur: string | null = null;
  private readonly resolvingIds = new Set<string>();
  private fixDepth = 0;
  private fixLabel: MessageRef | null = null;
  private shaderErrors: readonly ShaderErrorReport[] = [];

  constructor(deps: Deps, opts: ProblemsServiceOptions) {
    this.deps = deps;
    this.opts = opts;
    this.ruleList = opts.rules ?? CHECK_RULES;
    this.filterState = { ...parseFilter(deps.settings.getExt(FILTER_KEY)), query: '' };
    this.workerSubs.push(
      deps.analysis.on('problems', (d) => {
        this.onProblems(d);
      }),
      deps.analysis.on('needResync', () => {
        this.onNeedResync();
      }),
      deps.analysis.on('stats', (s) => {
        this.onStats(s);
      }),
      deps.analysis.onFailure(() => {
        this.workerFailed = true;
        this.passRunning = false;
        this.phiraRunning = false;
        this.changed();
      }),
      deps.settings.subscribe((s) => {
        this.onSettings(s);
      }),
    );
    this.syncStore();
  }

  // ---------------- contract ----------------

  problems(): readonly Problem[] {
    if (this.listCache === null || this.listDirty) this.rebuildList();
    return this.listCache ?? [];
  }

  counts(): Readonly<Record<Severity, number>> {
    this.countsCache ??= countBySeverity(this.problems());
    return this.countsCache;
  }

  subscribe(cb: () => void): Unsubscribe {
    this.syncStore();
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  next(dir: 1 | -1): Problem | null {
    const f = this.filterState;
    const sev = new Set(f.severities);
    const cat = new Set(f.categories);
    let list = this.problems().filter((p) => sev.has(p.severity) && cat.has(p.category));
    if (list.length === 0) list = [...this.problems()];
    if (list.length === 0) return null;
    const beat = (p: Problem): number | null => this.beatOf(p);
    list.sort(byTime(beat));
    const here = this.playheadBeat();
    const n = list.length;
    const curIdx = this.cur === null ? -1 : list.findIndex((p) => p.id === this.cur);
    let idx: number;
    const curProblem = curIdx >= 0 ? list[curIdx] : undefined;
    const curBeat = curProblem === undefined ? null : beat(curProblem);
    if (curProblem !== undefined && (curBeat === null || Math.abs(curBeat - here) <= SAME_BEAT)) {
      idx = (curIdx + dir + n) % n;
    } else if (dir > 0) {
      idx = list.findIndex((p) => (beat(p) ?? -Infinity) > here + SAME_BEAT);
      if (idx < 0) idx = 0;
    } else {
      idx = -1;
      for (let i = n - 1; i >= 0; i--) {
        const p = list[i] as Problem;
        if ((beat(p) ?? -Infinity) < here - SAME_BEAT) {
          idx = i;
          break;
        }
      }
      if (idx < 0) idx = n - 1;
    }
    const target = list[idx] as Problem;
    this.setCurrent(target.id);
    return target;
  }

  rules(): readonly Pick<CheckRule, 'id' | 'title' | 'category' | 'severity' | 'level'>[] {
    this.ruleInfo ??= this.ruleList.map((r) => ({
      id: r.id,
      title: r.title,
      category: r.category,
      severity: r.severity,
      level: r.level,
    }));
    return this.ruleInfo;
  }

  fullCheck(): void {
    this.syncStore();
    const store = this.store;
    if (store === null) return;
    this.storedProbe?.update(store.doc, store.idx, true);
    this.deps.analysis.post('fullCheck', {});
    this.setChecking(true);
  }

  async applyFix(problemId: string, fixIndex: number): Promise<boolean> {
    const p = this.get(problemId);
    if (p === undefined || this.ignoredKeys().includes(problemId)) return false;
    const fix = p.fixes[fixIndex];
    if (fix === undefined) return false;
    this.setCurrent(problemId);
    const s = this.services;
    if (s !== null) {
      const seqBefore = this.store?.seq ?? null;
      this.fixDepth++;
      this.fixLabel = fix.label;
      let ok: boolean;
      try {
        ok = await s.registry.runAny(fix.command, fix.args, 'fix');
      } finally {
        this.fixDepth--;
        if (this.fixDepth === 0) this.fixLabel = null;
      }
      // "Fixed, checking again" only when the fix committed a change (a failed fix toasts its reason).
      const changed = this.store !== null && this.store.seq !== seqBefore;
      if (ok && isFixCommand(fix.command) && changed) this.markResolving(problemId);
      return ok;
    }
    // Not attached to the app yet: the checker's own fixes still run, straight on the store.
    const store = this.deps.getStore();
    if (store === null || !isFixCommand(fix.command)) return false;
    const editing = this.deps.settings.value.editing;
    const out = await runFix(fix.command, fix.args, {
      store,
      snapDiv: editing.defaultSnapDiv,
      xGrid: editing.defaultXGrid,
      label: fix.label,
      media: null,
    });
    if (out.ok && out.txn !== null) this.markResolving(problemId);
    return out.ok;
  }

  // ---------------- extras ----------------

  get version(): number {
    return this.ver;
  }

  attach(s: AppServices): () => void {
    this.detachAll();
    this.services = s;
    this.detachers.push(
      s.onChange(() => {
        this.syncStore();
        this.invalidate();
      }),
      s.skins.subscribe(() => {
        this.sendEnvironment();
      }),
      s.editor.subscribe((st, prev) => {
        if (st.preview.player !== prev.preview.player) this.sendEnvironment();
        if (st.activeLine !== prev.activeLine && this.filterState.scope === 'line') this.changed();
      }),
    );
    this.syncStore();
    this.syncStoredProbe();
    this.sendEnvironment();
    this.startPoll();
    return () => {
      if (this.services === s) this.detachAll();
    };
  }

  dispose(): void {
    this.cancelNotify();
    this.detachAll();
    this.disconnectStore();
    for (const u of this.workerSubs.splice(0)) u();
    this.listeners.clear();
  }

  /**
   * ProblemsService.checking (CR-W2-09-6): the Phira rules of the pass that covers the latest change have not finished
   * on a connected, working analysis worker. Only they can block an export, so the export preflight waits for them
   * and not for the gameplay and style rules that follow (UX-092); `status().checking` covers the whole pass.
   */
  checking(): boolean {
    return this.phiraRunning && this.passRunning && !this.workerFailed && this.store !== null;
  }

  status(): CheckerStatus {
    return {
      connected: this.store !== null,
      checking: this.passRunning,
      checkingPhira: this.phiraRunning && this.passRunning,
      stats: this.stats,
      workerFailed: this.workerFailed,
      failedRules: this.failedRules,
    };
  }

  get(id: string): Problem | undefined {
    return this.byId.get(id) ?? this.storedProblems().find((p) => p.id === id);
  }

  filter(): ProblemFilter {
    return this.filterState;
  }

  setFilter(patch: Partial<ProblemFilter>): void {
    const next: ProblemFilter = { ...this.filterState, ...patch };
    if (sameJson(next, this.filterState)) return;
    const persist = patch.severities !== undefined || patch.categories !== undefined || patch.scope !== undefined;
    this.filterState = next;
    if (persist) {
      this.deps.settings.setExt(FILTER_KEY, {
        severities: [...next.severities],
        categories: [...next.categories],
        scope: next.scope,
      });
    }
    this.changed();
  }

  current(): string | null {
    return this.cur;
  }

  setCurrent(id: string | null): void {
    if (this.cur === id) return;
    this.cur = id;
    this.changed();
  }

  resolving(): ReadonlySet<string> {
    return this.resolvingIds;
  }

  fixing(): boolean {
    return this.fixDepth > 0;
  }

  pendingFixLabel(): MessageRef | null {
    return this.fixLabel;
  }

  beatOf(p: Problem): number | null {
    return problemBeatF64(p, this.tempo());
  }

  problemsAt(q: ProblemQuery): readonly Problem[] {
    return this.problems().filter((p) => {
      const at = p.at;
      if (q.field !== undefined && at.field !== q.field) return false;
      if (q.line !== undefined && at.line !== q.line && at.lines?.includes(q.line) !== true) return false;
      if (q.note !== undefined && at.notes?.includes(q.note) !== true) return false;
      if (q.key !== undefined && at.keys?.includes(q.key) !== true) return false;
      if (q.track !== undefined && at.tracks?.includes(q.track) !== true) return false;
      if (q.asset !== undefined && at.asset !== q.asset) return false;
      if (q.effect !== undefined && at.effect !== q.effect) return false;
      return true;
    });
  }

  fieldBadges(q: ProblemQuery): readonly BadgeProps[] {
    return this.problemsAt(q).map((p) => ({
      kind: 'problem' as const,
      severity: p.severity,
      tooltip: p.message,
      onClick: () => {
        this.setCurrent(p.id);
        this.services?.ui.showPanel('problems');
      },
    }));
  }

  async reveal(id: string): Promise<boolean> {
    const p = this.get(id);
    const s = this.services;
    if (p === undefined || s === null) return false;
    this.setCurrent(id);
    return revealProblem(s, p);
  }

  quickFixTarget(): Problem | null {
    const list = this.problems();
    if (this.cur !== null) {
      // The current problem, also without a fix: Ctrl+. then goes there (its "Go to" fix is left out, UX-093).
      const p = list.find((x) => x.id === this.cur);
      if (p !== undefined && (p.fixes.length > 0 || canGo(p))) return p;
    }
    const s = this.services;
    if (s === null) return null;
    const sel = s.editor.getState().selection;
    for (const p of list) {
      if (p.fixes.length === 0) continue;
      if (p.at.notes?.some((n) => sel.notes.has(n)) === true) return p;
      if (p.at.keys?.some((k) => sel.keys.has(k) || sel.segments.has(k)) === true) return p;
    }
    return null;
  }

  ignore(id: string): boolean {
    const p = this.get(id);
    const store = this.store;
    if (p === undefined || store === null) return false;
    const ok = ignoreProblemInDoc(store, p);
    if (ok) {
      if (this.cur === id) this.cur = null;
      this.invalidate();
      this.services?.ui.toast({ key: m.toastIgnored }, { kind: 'info', undo: true });
    }
    return ok;
  }

  unignore(keys: readonly string[]): boolean {
    const store = this.store;
    if (store === null) return false;
    const ok = unignoreInDoc(store, keys);
    if (ok) this.invalidate();
    return ok;
  }

  ignoredKeys(): readonly string[] {
    return this.store?.doc.editor.ignoredProblems ?? [];
  }

  reportShaderErrors(errors: readonly ShaderErrorReport[]): void {
    if (sameJson(errors, this.shaderErrors)) return;
    this.shaderErrors = [...errors];
    if (this.store !== null) this.postConfigure({ shaderErrors: this.shaderErrors });
  }

  // ---------------- worker events ----------------

  private onProblems(d: ProblemDiff): void {
    if (isResetDiff(d)) {
      // The worker started over (an init): everything it reported before is gone, whatever arrived meanwhile.
      this.awaitingReset = false;
      this.byId.clear();
      this.applyDiff({ added: d.added, removed: [], updated: d.updated });
      return;
    }
    if (this.awaitingReset || this.store === null) return;
    this.applyDiff(d);
  }

  private applyDiff(d: ProblemDiff): void {
    for (const id of d.removed) this.byId.delete(id);
    for (const p of d.added) this.byId.set(p.id, p);
    for (const p of d.updated) this.byId.set(p.id, p);
    if (this.cur !== null && !this.byId.has(this.cur)) this.cur = null;
    this.invalidate();
  }

  private onStats(raw: AnalysisWorkerEvents['stats']): void {
    if (this.awaitingReset || this.store === null) return;
    // `req` and `failedRules` are the stats extension of CR-W2-12a-4, `phase` of CR-W3-08-2 (absent from older
    // workers).
    const s: AnalysisStats = raw;
    this.workerFailed = false;
    // A pass that started before the latest request does not cover it: keep "checking" (and the fix notes).
    const covers = s.req === undefined || s.req >= this.reqSeq;
    if (s.phase === 'phira') {
      // Mid-pass: the Phira rules are done; the gameplay and style rules go on.
      if (covers && this.phiraRunning) {
        this.phiraRunning = false;
        this.changed();
      }
      return;
    }
    this.stats = s;
    this.failedRules = s.failedRules ?? NO_RULES;
    if (covers) {
      this.passRunning = false;
      this.phiraRunning = false;
      this.resolvingIds.clear();
    }
    this.changed();
  }

  private onNeedResync(): void {
    const store = this.store;
    if (store === null || this.awaitingReset) return;
    if (store.gestureActive) {
      // Uncommitted ops must never reach the replica: wait for the gesture to end (commit, cancel, or nothing).
      this.pendingResync = true;
      this.watchPendingResync();
      return;
    }
    this.postResync(store);
  }

  /** Sends a deferred resync once no gesture is live; true when nothing is pending any more. */
  private releasePendingResync(): boolean {
    const store = this.store;
    if (!this.pendingResync || store === null) return true;
    if (store.gestureActive) return false;
    this.postResync(store);
    return true;
  }

  private watchPendingResync(): void {
    if (this.resyncPoll !== null) return;
    const ms = this.opts.resyncPollMs ?? RESYNC_POLL_MS;
    this.resyncPoll = this.setTimerInterval(() => {
      if (this.releasePendingResync()) this.stopResyncPoll();
    }, ms);
  }

  private stopResyncPoll(): void {
    if (this.resyncPoll === null) return;
    this.clearTimerInterval(this.resyncPoll);
    this.resyncPoll = null;
  }

  // ---------------- store ----------------

  private syncStore(): void {
    const next = this.deps.getStore();
    if (next === this.store) return;
    this.disconnectStore();
    // CR-W3-01-5 (UX-023): a project that closes takes its problem list with it — nothing of the chart's problems
    // (the 30k chart holds about 43 000 items) may outlive the store that produced them.
    this.tempoCache = null;
    this.byId.clear();
    this.cur = null;
    this.resolvingIds.clear();
    this.stats = null;
    this.failedRules = NO_RULES;
    this.shaderErrors = [];
    if (next !== null) this.connectStore(next);
    this.invalidate();
  }

  /** The next request token. */
  private nextReq(): number {
    this.reqSeq++;
    return this.reqSeq;
  }

  private connectStore(store: DocStore): void {
    this.store = store;
    this.pendingResync = false;
    const env = this.environment();
    const settings = this.deps.settings.value;
    const enabled = enabledRuleIds(this.ruleList, settings.checker.disabledRules);
    // `level` is an init extension (CR-W2-12a-4): the first pass already leaves out Full-level rules in Guided.
    const init: Tokened<AnalysisWorkerSchema['init']['params']> & { readonly level: 'guided' | 'full' } = {
      projectId: store.projectId,
      doc: serializeDoc(store.doc),
      seq: store.seq,
      thresholds: settings.checker.thresholds,
      enabledRules: enabled,
      songLengthSec: env.songLengthSec,
      songLengthAsset: env.songLengthAsset,
      player: env.player,
      skin: env.skin,
      refViewport: { w: REFERENCE_VIEWPORT.w, h: REFERENCE_VIEWPORT.h },
      level: settings.general.level,
      req: this.nextReq(),
    };
    // A worker this service initialised before may still be sending the previous project's events.
    this.awaitingReset = this.initPosted;
    this.deps.analysis.post('init', init);
    this.initPosted = true;
    this.lastPostedSeq = store.seq;
    this.sent = {
      thresholds: settings.checker.thresholds,
      enabled,
      level: settings.general.level,
      player: env.player,
      skin: env.skin,
      songLengthSec: env.songLengthSec,
      songLengthAsset: env.songLengthAsset,
    };
    if (this.shaderErrors.length > 0) this.postConfigure({ shaderErrors: this.shaderErrors });
    this.setChecking(true);
    this.storeSubs = [
      store.subscribeCommits((txn) => {
        this.onCommit(store, txn);
      }),
      store.subscribe((cs) => {
        this.onStoreChange(store, cs);
      }),
    ];
    this.startPoll();
    this.syncStoredProbe();
  }

  private disconnectStore(): void {
    for (const u of this.storeSubs.splice(0)) u();
    this.pendingResync = false;
    this.stopResyncPoll();
    this.store = null;
    this.sent = null;
    this.passRunning = false;
    this.phiraRunning = false;
    this.stopPoll();
    this.syncStoredProbe();
  }

  private onCommit(store: DocStore, txn: Txn): void {
    if (store !== this.store || txn.seq <= this.lastPostedSeq) return;
    // The chart's files may have changed; an asset written again may have put missing bytes back (or renamed one).
    const probe = this.storedProbe;
    if (probe !== null) {
      const assets = touchesAssets(txn.ops);
      if (probe.update(store.doc, store.idx, assets) || (assets && probe.anyMissing())) this.invalidate();
    }
    if (this.pendingResync && !store.gestureActive) {
      this.postResync(store);
      return;
    }
    const msg: Tokened<AnalysisWorkerSchema['txn']['params']> = { seq: txn.seq, ops: txn.ops, req: this.nextReq() };
    this.deps.analysis.post('txn', msg);
    this.lastPostedSeq = txn.seq;
    this.setChecking(true);
    this.sendEnvironment();
  }

  private onStoreChange(store: DocStore, cs: ChangeSet): void {
    if (store !== this.store) return;
    if (cs.source === 'load') {
      this.postResync(store);
      this.storedProbe?.update(store.doc, store.idx, true);
      this.invalidate();
      return;
    }
    // A cancelled gesture (or any change once no gesture is live) releases a deferred resync at once.
    if (this.pendingResync && !store.gestureActive) this.postResync(store);
    if (cs.dirty.tempo) this.tempoCache = null;
    if (cs.dirty.editor || cs.dirty.tempo) this.invalidate();
  }

  private postResync(store: DocStore): void {
    this.pendingResync = false;
    this.stopResyncPoll();
    const msg: Tokened<AnalysisWorkerSchema['resync']['params']> = {
      doc: serializeDoc(store.doc),
      seq: store.seq,
      req: this.nextReq(),
    };
    this.deps.analysis.post('resync', msg);
    this.lastPostedSeq = store.seq;
    this.setChecking(true);
  }

  // ---------------- configuration ----------------

  /**
   * Player, skin and the decoded song length with the asset it belongs to (CheckContext.songLengthAsset): after the
   * music is replaced the engine may still hold the previous file, which the rules then ignore.
   */
  private environment(): {
    player: PhiraPlayerSettings;
    skin: SkinMetrics;
    songLengthSec: number | null;
    songLengthAsset: AssetId | null;
  } {
    const s = this.services;
    let skin: SkinMetrics = CHECK_ENVIRONMENT_DEFAULTS.skin;
    let player: PhiraPlayerSettings = this.deps.settings.value.preview.player;
    let songLengthSec: number | null = null;
    let songLengthAsset: AssetId | null = null;
    if (s !== null) {
      try {
        skin = s.skins.active.metrics;
      } catch {
        skin = CHECK_ENVIRONMENT_DEFAULTS.skin;
      }
      player = s.editor.getState().preview.player;
      const music = s.audio.music;
      songLengthSec =
        music !== null && Number.isFinite(music.durationSec) && music.durationSec > 0 ? music.durationSec : null;
      songLengthAsset = songLengthSec === null || music === null ? null : music.asset;
    }
    return { player, skin, songLengthSec, songLengthAsset };
  }

  private postConfigure(p: AnalysisWorkerSchema['configure']['params']): void {
    const params: Tokened<AnalysisWorkerSchema['configure']['params']> = { ...p, req: this.nextReq() };
    this.deps.analysis.post('configure', params);
    this.setChecking(true);
  }

  private sendEnvironment(): void {
    const sent = this.sent;
    if (this.store === null || sent === null) return;
    const env = this.environment();
    const patch: {
      player?: PhiraPlayerSettings;
      skin?: SkinMetrics;
      songLengthSec?: number | null;
      songLengthAsset?: AssetId | null;
    } = {};
    if (!sameJson(env.player, sent.player)) {
      patch.player = env.player;
      sent.player = env.player;
    }
    if (env.skin !== sent.skin && !sameJson(env.skin, sent.skin)) {
      patch.skin = env.skin;
      sent.skin = env.skin;
    }
    // Length and provenance travel together (a replaced song of the same length still changes the asset).
    if (env.songLengthSec !== sent.songLengthSec || env.songLengthAsset !== sent.songLengthAsset) {
      patch.songLengthSec = env.songLengthSec;
      patch.songLengthAsset = env.songLengthAsset;
      sent.songLengthSec = env.songLengthSec;
      sent.songLengthAsset = env.songLengthAsset;
    }
    if (Object.keys(patch).length > 0) this.postConfigure(patch);
  }

  private onSettings(s: UserSettings): void {
    const prevFilter = this.filterState;
    const persisted = parseFilter(s.ext[FILTER_KEY]);
    if (
      !sameJson(persisted, {
        severities: prevFilter.severities,
        categories: prevFilter.categories,
        scope: prevFilter.scope,
      })
    ) {
      this.filterState = { ...persisted, query: prevFilter.query };
      this.changed();
    }
    const sent = this.sent;
    if (this.store === null || sent === null) return;
    const patch: {
      thresholds?: CheckThresholds;
      enabledRules?: readonly string[];
      level?: 'guided' | 'full';
      player?: PhiraPlayerSettings;
    } = {};
    if (!sameJson(s.checker.thresholds, sent.thresholds)) {
      patch.thresholds = s.checker.thresholds;
      sent.thresholds = s.checker.thresholds;
    }
    const enabled = enabledRuleIds(this.ruleList, s.checker.disabledRules);
    if (!sameJson(enabled, sent.enabled)) {
      patch.enabledRules = enabled;
      sent.enabled = enabled;
    }
    if (s.general.level !== sent.level) {
      patch.level = s.general.level;
      sent.level = s.general.level;
    }
    if (this.services === null && !sameJson(s.preview.player, sent.player)) {
      patch.player = s.preview.player;
      sent.player = s.preview.player;
    }
    if (Object.keys(patch).length > 0) this.postConfigure(patch);
  }

  private setTimerInterval(fn: () => void, ms: number): unknown {
    const set = this.opts.setInterval ?? ((f: () => void, t: number) => globalThis.setInterval(f, t));
    return set(fn, ms);
  }

  private clearTimerInterval(handle: unknown): void {
    if (this.opts.clearInterval !== undefined) this.opts.clearInterval(handle);
    else globalThis.clearInterval(handle as ReturnType<typeof globalThis.setInterval>);
  }

  private startPoll(): void {
    const ms = this.opts.pollMs ?? SONG_POLL_MS;
    if (this.poll !== null || ms <= 0 || this.store === null || this.services === null) return;
    this.poll = this.setTimerInterval(() => {
      this.sendEnvironment();
    }, ms);
  }

  private stopPoll(): void {
    if (this.poll === null) return;
    this.clearTimerInterval(this.poll);
    this.poll = null;
  }

  // ---------------- helpers ----------------

  private detachAll(): void {
    for (const u of this.detachers.splice(0)) u();
    this.services = null;
    this.stopPoll();
    this.syncStoredProbe();
  }

  // ---------------- files missing from this browser's storage ----------------

  /** One probe per open chart while the app is attached (it holds the persistence client). */
  private syncStoredProbe(): void {
    const store = this.store;
    const persist = this.services?.persist ?? null;
    if (store === null || persist === null) {
      if (this.storedProbe !== null) {
        this.storedProbe.dispose();
        this.storedProbe = null;
        this.stopStoredPoll();
        this.invalidate();
      }
      return;
    }
    if (this.storedProbe !== null) return;
    const probe = new StoredFilesProbe(
      (id) => persist.hasAsset(id),
      () => {
        if (this.storedProbe !== probe) return;
        if (probe.anyMissing()) this.startStoredPoll();
        else this.stopStoredPoll();
        this.invalidate();
      },
    );
    this.storedProbe = probe;
    probe.update(store.doc, store.idx, false);
  }

  /** The fatal problems of the used files whose stored bytes are missing. */
  private storedProblems(): readonly Problem[] {
    if (this.storedCache === null) {
      const store = this.store;
      const probe = this.storedProbe;
      const out: Problem[] = [];
      if (store !== null && probe !== null) {
        for (const id of probe.missing()) {
          const p = storedMissingProblem(store.doc, id);
          if (p !== null) out.push(p);
        }
      }
      this.storedCache = out;
    }
    return this.storedCache;
  }

  private startStoredPoll(): void {
    if (this.storedPoll !== null) return;
    this.storedPoll = this.setTimerInterval(() => {
      this.storedProbe?.recheckMissing();
    }, STORED_RECHECK_MS);
  }

  private stopStoredPoll(): void {
    if (this.storedPoll === null) return;
    this.clearTimerInterval(this.storedPoll);
    this.storedPoll = null;
  }

  private tempo(): TempoMap | null {
    const store = this.store;
    const engine = this.services?.engine ?? null;
    const key: unknown = engine !== null ? engine.tempo : store === null ? null : store.version('bpm');
    if (this.tempoCache === null || this.tempoCache.key !== key) {
      this.tempoCache = { key, map: tempoOf(this.services, store) };
    }
    return this.tempoCache.map;
  }

  private playheadBeat(): number {
    const tempo = this.tempo();
    if (tempo === null) return 0;
    return tempo.beatF64At(playheadSec(this.services));
  }

  private markResolving(id: string): void {
    this.resolvingIds.add(id);
    this.setChecking(true);
  }

  private setChecking(on: boolean): void {
    if (this.passRunning === on && this.phiraRunning === on) {
      if (on) this.changed();
      return;
    }
    this.passRunning = on;
    this.phiraRunning = on;
    this.changed();
  }

  private invalidate(): void {
    this.storedCache = null;
    this.countsCache = null;
    this.listDirty = true;
    // CR-W3-03-11: rebuild here, keeping the previous list until the new one is built, so a consumer mid-pass
    // never sees a null cache and the React render never pays the sort of the whole problem list. A rebuild
    // that yields the same ids keeps the array identity (groupTicks and useProblemList key on it).
    this.rebuildList();
    this.scheduleNotify();
  }

  /** Builds the list into `listCache` (build-then-swap; identity-preserving when the ids are unchanged). */
  private rebuildList(): void {
    const ignored = new Set(this.ignoredKeys());
    const beat = (p: Problem): number | null => this.beatOf(p);
    const next = [...this.byId.values(), ...this.storedProblems()]
      .filter((p) => !ignored.has(p.id))
      .sort(bySeverity(beat));
    const prev = this.listCache;
    this.listDirty = false;
    // Keep the previous array only when the rebuild produced the very same objects in the same order (so a
    // changed problem — new message, new severity — is never hidden behind a kept identity).
    if (prev !== null && sameList(prev, next)) {
      this.listCache = prev;
      return;
    }
    this.listCache = next;
  }

  /**
   * Coalesces list notifications behind one animation frame (CR-W3-03-11): a stress chart's first pass streams
   * problem batches and must not re-render the inspector once per batch. The list itself is already fresh for a
   * synchronous reader (`invalidate` rebuilds it eagerly, keeping the previous one until the new is built) — only
   * the notification waits for the frame, so N batches in one frame cost one render instead of N. Falls back to
   * a 0 ms timer where there is no `requestAnimationFrame`.
   */
  private scheduleNotify(): void {
    if (this.notifyPending) return;
    this.notifyPending = true;
    const run = (): void => {
      this.notifyPending = false;
      this.notifyHandle = null;
      this.changed();
    };
    const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => unknown }).requestAnimationFrame;
    if (typeof raf === 'function') this.notifyHandle = raf(run);
    else this.notifyHandle = setTimeout(run, 0);
  }

  private cancelNotify(): void {
    if (!this.notifyPending) return;
    this.notifyPending = false;
    const handle = this.notifyHandle;
    this.notifyHandle = null;
    if (handle === null || handle === undefined) return;
    const caf = (globalThis as { cancelAnimationFrame?: (h: number) => void }).cancelAnimationFrame;
    if (typeof caf === 'function') caf(handle as number);
    else clearTimeout(handle as ReturnType<typeof setTimeout>);
  }

  private changed(): void {
    this.ver++;
    for (const cb of [...this.listeners]) cb();
  }
}

/** The checker's problems service with explicit options (tests: rule set, polling). */
export function createCheckerProblemsService(deps: Deps, opts: ProblemsServiceOptions = {}): CheckerProblemsService {
  return new CheckerProblemsServiceImpl(deps, opts);
}

export const createProblemsService: CreateProblemsService = (deps) => createCheckerProblemsService(deps);
